import {
  AlertCircle,
  Building2,
  Calendar,
  Check,
  Download,
  FileSpreadsheet,
  Layers,
  Loader2,
  Play,
  Printer,
  RotateCcw,
  Save,
  SlidersHorizontal,
  Sparkles,
  Stethoscope,
  Table2,
  Tags,
  UserRound,
} from 'lucide-react';
import { DatePicker } from '../../../../components/ui/DatePicker';
import { SearchableSelect, type Option } from '../../../../components/ui/searchable-select';
import type { PeriodSelection, QuickRange } from '../../../../utils/reportPeriod';
import { MONTH_NAMES } from '../../../../utils/reportPeriod';
import type { ReportDefinition, ReportFilterKey, ReportPrintOptions } from '../../../../types/reports';
import { countHiddenPrintItems } from '../../../../utils/reportPrintOptions';
import { reportCategoryIcon } from '../reportIcons';

interface ReportConfigPanelProps {
  selectedReport: ReportDefinition;
  groupBy: string;
  isCategoryGroup: boolean;
  supports: (filter: ReportFilterKey) => boolean;
  activeFilterCount: number;
  dateFrom: string;
  dateTo: string;
  /** Which date the period applies to (document date or settlement date). */
  dateBasis: 'document' | 'settled';
  onDateBasisChange: (value: 'document' | 'settled') => void;
  /** Hospital timezone (Settings → General) for the pickers' "Today" shortcut. */
  timezone: string;
  period: PeriodSelection;
  ranges: QuickRange[];
  yearOptions: number[];
  dateError: string | null;
  statementDate: string;
  onApplyRange: (from: string, to: string) => void;
  onPeriodChange: (month: number | 'all', year: number) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onGroupByChange: (value: string) => void;
  onReset: () => void;
  onRun: () => void;
  running: boolean;
  onSaveStatement: () => void;
  savingStatement: boolean;
  onExport: (format: 'pdf' | 'excel' | 'csv') => void;
  exporting: boolean;
  exportFormat?: string;
  /** The saved print layout for this report (shared by all users). */
  printOptions: ReportPrintOptions;
  /** Opens the modal that chooses/renames what the PDF / print copy contains. */
  onOpenPrintOptions: () => void;
  /** `false` when the saved layout disables every block — PDF/Print are blocked. */
  hasPrintSection: boolean;
  onPrint: () => void;
  printing: boolean;
  canExport: boolean;
  categoryId: string;
  categoryOptions: Option[];
  categoryLoading: boolean;
  onCategoryChange: (value: string) => void;
  serviceType: string;
  serviceTypeOptions: Option[];
  serviceTypeLoading: boolean;
  onServiceTypeChange: (value: string) => void;
  diagnosticCategory: string;
  diagnosticCategoryOptions: Option[];
  diagnosticCategoryLoading: boolean;
  onDiagnosticCategoryChange: (value: string) => void;
  departmentId: string;
  departmentOptions: Option[];
  departmentLoading: boolean;
  onDepartmentChange: (value: string) => void;
  doctorId: string;
  doctorOptions: Option[];
  doctorLoading: boolean;
  onDoctorChange: (value: string) => void;
  patientId: string;
  patientOptions: Option[];
  patientLoading: boolean;
  onPatientChange: (value: string) => void;
  onPatientSearchChange: (value: string) => void;
}

/** Filter form + action bar for the selected report. */
export function ReportConfigPanel(props: ReportConfigPanelProps) {
  const {
    selectedReport,
    groupBy,
    isCategoryGroup,
    supports,
    activeFilterCount,
    dateFrom,
    dateTo,
    dateBasis,
    timezone,
    period,
    ranges,
    yearOptions,
    dateError,
    statementDate,
  } = props;

  const SelectedIcon = reportCategoryIcon(selectedReport.category);
  const fieldLabel = 'mb-1 flex items-center gap-1.5 text-[11px] font-medium text-text-muted';
  const controlClass =
    'h-9 w-full rounded-lg border border-border bg-surface px-2.5 text-sm text-text transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40';
  const hasScopeFilters =
    supports('category') ||
    supports('serviceType') ||
    supports('diagnosticCategory') ||
    supports('department') ||
    supports('doctor') ||
    supports('patient');
  // Badge text for the print-options button: how much is customised.
  const hiddenPrintItems = countHiddenPrintItems(props.printOptions);
  const enabledPrintBlocks = [
    props.printOptions.summary,
    props.printOptions.breakdowns,
    props.printOptions.details && selectedReport.printWithoutDetails !== true,
  ].filter(Boolean).length;

  const handleMonthChange = (value: string) => {
    props.onPeriodChange(value === 'all' ? 'all' : Number(value), period.year);
  };

  const handleYearChange = (value: string) => {
    const year = Number(value);
    props.onPeriodChange(
      period.month === 'all' || period.month === 'custom' ? 'all' : period.month,
      year,
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
      {/* Report header */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border bg-gradient-to-r from-primary/5 via-transparent to-transparent px-4 py-3.5 sm:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-sm shadow-primary/25">
            <SelectedIcon size={19} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold leading-tight text-text">{selectedReport.name}</h2>
              <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                {selectedReport.category}
              </span>
            </div>
            <p className="mt-0.5 line-clamp-2 text-xs text-text-muted">{selectedReport.description}</p>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-text-muted shadow-sm">
            <SlidersHorizontal size={12} />
            {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} active
          </span>
        )}
      </div>

      <div className="space-y-4 p-4 sm:p-5">
        {/* Report type */}
        {selectedReport.groupByOptions && selectedReport.groupByOptions.length > 0 && (
          <div className="space-y-2">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              <Layers size={13} />
              Report type
            </span>
            <div className="inline-flex max-w-full flex-wrap gap-1 rounded-xl border border-border bg-background/60 p-1">
              {selectedReport.groupByOptions.map((option) => {
                const isActive = groupBy === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => props.onGroupByChange(option.value)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all ${
                      isActive
                        ? 'bg-surface text-primary shadow-sm ring-1 ring-border'
                        : 'text-text-muted hover:text-text'
                    }`}
                  >
                    {isActive && <Check size={12} />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Period */}
        {supports('date') && (
          <div className="rounded-xl border border-border/70 bg-background/40 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                <Calendar size={13} />
                Period
              </span>
              <div className="flex flex-wrap gap-1">
                {ranges.map((range) => {
                  const isActive = dateFrom === range.from && dateTo === range.to;
                  return (
                    <button
                      key={range.label}
                      type="button"
                      onClick={() => props.onApplyRange(range.from, range.to)}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        isActive
                          ? 'border-primary bg-primary text-white shadow-sm shadow-primary/20'
                          : 'border-border bg-surface text-text-muted hover:border-primary/40 hover:text-text'
                      }`}
                    >
                      {range.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-2 lg:grid-cols-4">
              <div>
                <label className={fieldLabel}>Month</label>
                <select
                  value={period.month === 'custom' ? 'custom' : String(period.month)}
                  onChange={(event) => handleMonthChange(event.target.value)}
                  className={controlClass}
                >
                  {period.month === 'custom' && (
                    <option value="custom" disabled>
                      Custom range
                    </option>
                  )}
                  <option value="all">Full Year</option>
                  {MONTH_NAMES.map((name, index) => (
                    <option key={name} value={index}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={fieldLabel}>Year</label>
                <select
                  value={period.year}
                  onChange={(event) => handleYearChange(event.target.value)}
                  className={controlClass}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={fieldLabel}>Date from</label>
                <DatePicker
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={props.onDateFromChange}
                  className={controlClass}
                  timeZone={timezone}
                />
              </div>
              <div>
                <label className={fieldLabel}>Date to</label>
                <DatePicker
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={props.onDateToChange}
                  className={controlClass}
                  timeZone={timezone}
                />
              </div>
            </div>
            {supports('dateBasis') && (
              <div className="mt-2.5 flex flex-wrap items-center gap-2 border-t border-border/60 pt-2.5">
                <span className="text-[11px] font-medium text-text-muted">Date basis</span>
                <div className="inline-flex gap-1 rounded-lg border border-border bg-surface p-0.5">
                  {([
                    { value: 'document', label: 'Document date' },
                    { value: 'settled', label: 'Settled On' },
                  ] as const).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => props.onDateBasisChange(option.value)}
                      className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        dateBasis === option.value
                          ? 'bg-primary text-white'
                          : 'text-text-muted hover:text-text'
                      }`}
                      title={
                        option.value === 'settled'
                          ? 'Count a document on the day its balance was fully paid'
                          : 'Count a document on the day it was created'
                      }
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <span className="text-[11px] text-text-muted">
                  {dateBasis === 'settled'
                    ? 'Money collected against an earlier bill appears in the period it was settled.'
                    : 'Documents are counted in the period they were created.'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Scope filters */}
        {hasScopeFilters && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                <SlidersHorizontal size={13} />
                Scope
              </span>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={props.onReset}
                  className="text-[11px] font-medium text-text-muted transition-colors hover:text-danger"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {supports('category') && (
                <div>
                  <label className={fieldLabel}>
                    <Tags size={12} />
                    Category
                  </label>
                  <SearchableSelect
                    value={props.categoryId}
                    onChange={props.onCategoryChange}
                    options={props.categoryOptions}
                    loading={props.categoryLoading}
                    placeholder="All categories"
                    disabled={isCategoryGroup}
                    inputClassName="min-h-9 py-1.5 bg-surface"
                  />
                </div>
              )}

              {supports('serviceType') && (
                <div>
                  <label className={fieldLabel}>
                    <Layers size={12} />
                    Type
                  </label>
                  <SearchableSelect
                    value={props.serviceType}
                    onChange={props.onServiceTypeChange}
                    options={props.serviceTypeOptions}
                    loading={props.serviceTypeLoading}
                    placeholder="All types"
                    inputClassName="min-h-9 py-1.5 bg-surface"
                  />
                </div>
              )}

              {supports('diagnosticCategory') && (
                <div>
                  <label className={fieldLabel}>
                    <Tags size={12} />
                    Diagnostic category
                  </label>
                  <SearchableSelect
                    value={props.diagnosticCategory}
                    onChange={props.onDiagnosticCategoryChange}
                    options={props.diagnosticCategoryOptions}
                    loading={props.diagnosticCategoryLoading}
                    placeholder="All categories"
                    inputClassName="min-h-9 py-1.5 bg-surface"
                  />
                </div>
              )}

              {supports('department') && (
                <div>
                  <label className={fieldLabel}>
                    <Building2 size={12} />
                    Department
                  </label>
                  <SearchableSelect
                    value={props.departmentId}
                    onChange={props.onDepartmentChange}
                    options={props.departmentOptions}
                    loading={props.departmentLoading}
                    placeholder="All departments"
                    inputClassName="min-h-9 py-1.5 bg-surface"
                  />
                </div>
              )}

              {supports('doctor') && (
                <div>
                  <label className={fieldLabel}>
                    <Stethoscope size={12} />
                    Doctor
                  </label>
                  <SearchableSelect
                    value={props.doctorId}
                    onChange={props.onDoctorChange}
                    options={props.doctorOptions}
                    loading={props.doctorLoading}
                    placeholder="All doctors"
                    inputClassName="min-h-9 py-1.5 bg-surface"
                  />
                </div>
              )}

              {supports('patient') && (
                <div>
                  <label className={fieldLabel}>
                    <UserRound size={12} />
                    Patient
                  </label>
                  <SearchableSelect
                    value={props.patientId}
                    onChange={props.onPatientChange}
                    options={props.patientOptions}
                    loading={props.patientLoading}
                    placeholder="All patients"
                    onQueryChange={props.onPatientSearchChange}
                    inputClassName="min-h-9 py-1.5 bg-surface"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {!hasScopeFilters && !supports('date') && (
          <div className="flex items-center gap-2.5 rounded-xl border border-dashed border-border bg-background/40 px-3 py-3 text-sm text-text-muted">
            <Sparkles size={16} className="shrink-0 text-primary" />
            This report does not require any filters — just generate it.
          </div>
        )}

        {dateError && (
          <div className="flex items-center gap-2 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger">
            <AlertCircle size={15} />
            <span>{dateError}</span>
          </div>
        )}

        {selectedReport.id === 'daily-statement' && (
          <div className="flex items-start gap-2 rounded-xl border border-primary/20 bg-primary/[0.06] px-3 py-2.5 text-xs text-text-muted">
            <Calendar size={15} className="mt-0.5 shrink-0 text-primary" />
            <span>
              Shows the archived snapshot for{' '}
              <span className="font-semibold text-text">{statementDate}</span> when one exists,
              otherwise a live calculation. Statements are frozen automatically every night; use{' '}
              <span className="font-semibold text-text">Save day snapshot</span> to freeze (or
              regenerate) this day now. Every section is labelled with its accounting basis (accrual,
              cash, or point-in-time balance).
            </span>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border bg-background/40 px-4 py-3 sm:px-5">
        <button
          type="button"
          onClick={props.onRun}
          disabled={props.running}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3.5 text-sm font-semibold text-white shadow-sm shadow-primary/25 transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {props.running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
          Generate report
        </button>
        {selectedReport.id === 'daily-statement' && (
          <button
            type="button"
            onClick={props.onSaveStatement}
            disabled={props.savingStatement || props.running}
            title={`Freeze the statement for ${statementDate}`}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {props.savingStatement ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            Save day snapshot
          </button>
        )}
        <button
          type="button"
          onClick={props.onReset}
          disabled={props.running}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-text-muted transition-colors hover:text-text disabled:opacity-50"
        >
          <RotateCcw size={14} />
          Reset
        </button>

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <span className="hidden text-[10px] font-semibold uppercase tracking-wide text-text-muted sm:inline">
            PDF / Print
          </span>
          {/* The saved layout decides which blocks/items print and how they are
              labelled. The modal edits it; the PDF and Print actions read it
              from the API, so every workstation prints the same document. */}
          <button
            type="button"
            onClick={props.onOpenPrintOptions}
            title="Choose which sections, cards and breakdowns appear in the PDF and printed copy, and rename their labels"
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 text-[11px] font-medium text-text-muted shadow-sm transition-colors hover:border-primary/40 hover:text-text"
          >
            <SlidersHorizontal size={13} />
            Print options
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                hiddenPrintItems > 0
                  ? 'bg-primary/10 text-primary'
                  : 'bg-background text-text-muted'
              }`}
            >
              {hiddenPrintItems > 0 ? `${hiddenPrintItems} hidden` : `${enabledPrintBlocks}/3`}
            </span>
          </button>
          {!props.hasPrintSection && (
            <span className="text-[11px] font-medium text-danger">
              Select at least one section
            </span>
          )}
          <span className="hidden text-[10px] font-semibold uppercase tracking-wide text-text-muted sm:inline">
            Export
          </span>
          <div className="flex items-center gap-0.5 rounded-lg border border-border bg-surface p-0.5 shadow-sm">
            <ExportButton
              label="PDF"
              icon={Download}
              pending={props.exporting && props.exportFormat === 'pdf'}
              disabled={!props.canExport || props.exporting || !props.hasPrintSection}
              onClick={() => props.onExport('pdf')}
            />
            <ExportButton
              label="Excel"
              icon={FileSpreadsheet}
              pending={props.exporting && props.exportFormat === 'excel'}
              disabled={!props.canExport || props.exporting}
              onClick={() => props.onExport('excel')}
            />
            <ExportButton
              label="CSV"
              icon={Table2}
              pending={props.exporting && props.exportFormat === 'csv'}
              disabled={!props.canExport || props.exporting}
              onClick={() => props.onExport('csv')}
            />
            <ExportButton
              label="Print"
              icon={Printer}
              pending={props.printing}
              disabled={!props.canExport || props.printing || props.exporting || !props.hasPrintSection}
              onClick={props.onPrint}
              title="Open the printable A4 copy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ExportButton({
  label,
  icon: Icon,
  pending,
  disabled,
  onClick,
  title,
}: {
  label: string;
  icon: typeof Download;
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-text transition-colors hover:bg-background disabled:cursor-not-allowed disabled:opacity-40"
    >
      {pending ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />}
      {label}
    </button>
  );
}
